/**
 * Features is the same for both domain and one-core. It could be merged with commonTypes, but separating it made a little more order
 */
import {
  AnalyticsEventType,
  PaywallConfig,
  PluginType,
} from '@accedo/one-plugin-sdk';
import { Action } from '@dynamic/ui';

export type FeatureConfig<TProps> = {
  enabled: boolean;
  key: string;
  plugin?: {
    type: PluginType;
    id: string;
    src: string;
  };
  properties: TProps;
};

export type FeatureArrayData<TProps> = FeatureConfig<TProps>[];

export type FeatureSearchProps = {
  placeholder: string;
  preSearchPageId: string;
  autoSearchDelayMs: number;
  minSearchLength: number;
};

export type FeaturePlayerProps = {
  // all players can have these
  subtitleTypeFilter?: string;
  // any player can have anything else
  [key: string]: unknown;
};

type StructuredEvent = {
  // outgoing event name
  event: string;
  // any event properties
  properties?: Record<string, unknown>;
  // not provided initially, be we add it automatically
  coreEventName?: string;
  // any additional fields
  [key: string]: unknown;
};

export type FeatureAnalyticsProps = {
  showDebugLogs?: boolean;
  events?: { [key in AnalyticsEventType]?: string[] };
  valueOverrides?: { [key: string]: unknown };
  // V3
  structuredEvents?: Partial<Record<AnalyticsEventType, StructuredEvent[]>>;
  // There are plugin specific other properties
  [key: string]: unknown;
};

export type FeatureAdsProps = {
  keyValues?: { [key: string]: string };
  // There are plugin specific other properties
  [key: string]: unknown;
};

export type FeaturePageAdsProps = {};

export type FeaturePrivacyProps = {
  showDebugLogs?: boolean;
  disableFloatingButton?: boolean;
  // There are plugin specific other properties
  [key: string]: unknown;
};

export interface FeaturePaywallProps extends PaywallConfig {
  sessionEndpoint?: string;
  // There are plugin specific other properties
  [key: string]: unknown;
}

export type RawFeaturesConfig = {
  // This should be removed but for backwards compatibility we need to hold onto it until all analytics are sent as integrations via runtime config.
  analytics?: FeatureArrayData<FeatureAnalyticsProps>;
  videoPlayer?: FeatureArrayData<FeaturePlayerProps>;
  paywall?: FeatureArrayData<FeaturePaywallProps>;
  ads?: FeatureArrayData<FeatureAdsProps>;
  pageAds?: FeatureArrayData<FeaturePageAdsProps>;
  privacy?: FeatureArrayData<FeaturePrivacyProps>;
};

export type FeaturesConfig = RawFeaturesConfig & {
  search: CoreSearchFeature;
  playback: CorePlaybackFeature;
  purchase: CorePurchaseFeature;
};

export interface EventBatchConfig {
  endpoint: string;
  maxEventAge: string;
  maxEventBatchBodySize: string;
  maxEventBatchBodySizeInBytes: number;
  maxEventsInBatch: number;
  maxWaitBeforeFlush: string;
}

export interface CoreAnalyticsFeature {
  eventBatch?: EventBatchConfig;
}

export interface CoreFeatureConfig {
  analytics?: CoreAnalyticsFeature;
  authentication?: CoreAuthFeature;
  playback?: CorePlaybackFeature;
  purchase?: CorePurchaseFeature;
  search?: CoreSearchFeature;
  unauthenticatedHomepage?: CoreUnauthenticatedHomepageFeature;
  seo?: CoreSeoFeature;
  epg?: CoreEpgFeature;
  footer?: CoreFooterFeature;
  parentalControl?: CoreParentalControlFeature;
  crashReporting?: CoreCrashReportingFeature;
  errorReporting?: CoreErrorReportingFeature;
  sentry?: CoreSentryFeature;
}

interface CoreSentryFeature {
  dsn: string;
}

interface CoreCrashReportingFeature {
  enabled: boolean;
  attributes: {
    exclude: string[];
  };
}

interface CoreErrorReportingFeature {
  enabled: boolean;
  attributes: {
    exclude: string[];
  };
  errorCodes: {
    ignore: string[];
  };
}

interface CoreParentalControlFeature {
  parentalPin: {
    enabled: boolean;
    length: number;
    preferenceKey: string;
    restrictedContent: {
      level: string;
      preferenceKey: string;
      ratings: { type: string; values: string[] }[];
    };
  };
}

export enum SocialKey {
  FACEBOOK = 'FACEBOOK',
  TWITTER = 'TWITTER',
  YOUTUBE = 'YOUTUBE',
  LINKED_IN = 'LINKED_IN',
  INSTAGRAM = 'INSTAGRAM',
  TIKTOK = 'TIKTOK',
  PINTEREST = 'PINTEREST',
}

export interface CoreFooterFeatureItem {
  key: string;
  display: {
    title: string;
  };
  action: Action;
}
export interface CoreFooterFeature {
  copyright: string;
  contact: string;
  socials: {
    key: keyof typeof SocialKey;
    action: Action;
  }[];
  sections: {
    key: string;
    display: { title: string };
    items: CoreFooterFeatureItem[];
  }[];
}

export interface CoreEpgFeature {
  enabled: boolean;
  programList?: {
    channelOverviewEnabled: boolean;
    dateSelectorDateInterval: {
      future: number;
      past: number;
    };
  };
}

export interface CoreSeoFeature {
  defaultImage?: string;
  serviceTitle?: string;
}

export interface CorePlaybackFeature {
  /** @sample '12s' Time in between 2 playback beacons (sending progress and refreshing playback concurrency slots) */
  beaconInterval: string;

  stillWatching: {
    enabled: boolean;
    /** @sample '7200s' Time passed since the last user interaction. After this inactivity if the current playback reached the end the inactivity modal is shown to the user */
    inactivityThreshold: string;
  };
  stillWatchingLive: {
    enabled: boolean;
    /** @sample '7200s' Time passed since the last user interaction. After this inactivity if the current playback reached the end the inactivity modal is shown to the user */
    inactivityThreshold?: string;
  };
}

export interface CoreAuthFeature {
  secondScreen?: CoreAuthSecondScreen;
  standard?: CoreAuthStandard;
  tve?: CoreAuthTve;
}

export type CorePaymentMethod =
  | 'PAYMENT_METHOD_CREDIT_CARD'
  | 'PAYMENT_METHOD_PAYPAL'
  | 'PAYMENT_METHOD_UNKNOWN';

interface CorePurchaseSuccessScreen {
  buttons: {
    action?: Action;
    display: {
      title: string;
    };
  }[];
}

export interface CorePurchaseFeature {
  inApp: {
    enabled: boolean;
    successScreen?: CorePurchaseSuccessScreen;
    planPickerScreen?: {
      showSkipPurchaseButton: boolean;
    };
  };
  gifting?: {
    // this is strictly for gift purchase, not for redeem.
    anonymousAllowed: boolean;
    enabled: boolean;
    images: {
      landingPageBackgroundImage?: string;
      planSelectionImage?: string;
    };
    purchase?: {
      allowedPaymentMethods: CorePaymentMethod[];
    };
    redemption?: {
      allowedPaymentMethods: CorePaymentMethod[];
    };
  };
  images?: {
    saveCampaignModalBackground?: string;
    successfulPurchaseBackground?: string;
  };
}

export interface CoreSearchFeature {
  /** Search field behavior (search-as-you-go) */
  autoSubmit: {
    /** @sample '0.1s' Time to wait for the search to get submitted after the minimum search query length is met (debounce delay). */
    delay: string;

    /** Whether to automatically trigger search or not as the user types, or to wait for him to press the Search button. */
    enabled: boolean;

    /** The number of characters the user has to enter before the auto-search is triggered if enabled. */
    minimumSearchQueryLength: number;
  };
}

interface CoreUnauthenticatedHomepageFeature {
  enabled: boolean;
}

export enum ForgotPasswordSubmitBehavior {
  /** SPLIT - wait for click on link in email */
  Split = 'SPLIT',

  /** DIRECT_TO_UPDATE_PASSWORD_FORM - go to update password form and ask user to input password reset code on device */
  Direct = 'DIRECT_TO_UPDATE_PASSWORD_FORM',
}

interface OnWeb {
  /** User-friendly (shorter) URL that can be displayed to the user, that may skip some optional parameters or https://www. prefixes. */
  displayUrl?: string;
  /** QR code image URL that encodes the 'url' to the form. */
  qrCodeUrl?: string;
  /** URL to the form that where the user can complete the authentication flow. */
  url?: string;
}

interface CoreAuthStandard {
  enabled: boolean;
  forgotPassword?: {
    enabled: boolean;
    /** If true, forgot password flows can be completed on the device itself */
    onDevice: boolean;
    /** If set, the forgot password flow may be completed by sending the user to the specified URL. Web client is expected to redirect to that URL */
    onWeb?: OnWeb;
    submitBehavior: ForgotPasswordSubmitBehavior;
  };
  login?: {
    enabled: boolean;
    /** If true, login can be completed on the device itself */
    onDevice: boolean;
    /** If true, login can be completed on a second screen device, by requesting a second screen code. */
    onSecondScreen: boolean;
  };
  registration?: {
    enabled: boolean;
    /** If true, registration can be completed on the device itself */
    onDevice: boolean;
    showSkipPurchaseButton?: boolean;
    /** If set, the registration flow may be completed by sending the user to the specified URL. Web client is expected to redirect to that URL */
    onWeb?: OnWeb;
    /** Background image to use on desktop and tablet. Also enables the modal-like style. */
    backgroundImage?: string;
  };
}

interface CoreAuthTve {
  enabled: boolean;
  /** If true, TVE authentication can be completed on the device itself (the form should be rendered on the device directly, and the SSO calls should be performed in the device's browser). */
  onDevice: boolean;
  /** If true, TVE authentication can be completed on a second screen device, by requesting a second screen code. */
  onSecondScreen: boolean;
  images?: {
    background?: string;
  };
}

interface CoreAuthSecondScreen {
  /** The length of the second-screen codes generated by one-core, so that the input field on the second screen device may be adjusted and pre-validated. */
  codeLength: number;
  enabled: boolean;
  images?: { webAuthBackground?: string };
}

export interface CoreSearchFeature {
  /** Search field behavior (search-as-you-go) */
  autoSubmit: {
    /** @sample '0.1s' Time to wait for the search to get submitted after the minimum search query length is met (debounce delay). */
    delay: string;

    /** Whether to automatically trigger search or not as the user types, or to wait for him to press the Search button. */
    enabled: boolean;

    /** The number of characters the user has to enter before the auto-search is triggered if enabled. */
    minimumSearchQueryLength: number;
  };
}
